import NumberOfColumn from "@components/global/new/NumberOfColumn/NumberOfColumn";
import { useConnectionsActions3 } from "@mobx/hooks/useConnectionsActions3";
import { useWatchNetwork } from "@mobx/services/http";
import mainLocationsStore from "@mobx/store/locations";
import { IRack } from "@mobx/store/locations/types";
import { EQUIPMENT_TYPE } from "@store/Equipments/actionTypes";
import { makeGrid } from "@utils/makeGrid";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { memo, useEffect, useRef, useState } from "react";
import { RACK_UNIT, SHOW_SCHEMES } from "../../../config";
import BackIcon3 from "../BackIcon3";
import RackHeader3 from "../RackHeader3";
import RackItem3 from "../RackItem3";
import LightRackItem3 from "../RackItem3/LightRackItem3";
import styles from "./Rack.module.scss";

export interface IRack3Props {
  rack: IRack;
}

const useControl = (props: IRack3Props) => {
  const [heavy, setHeavy] = useState(false);
  const self = useRef<HTMLDivElement | null>(null);
  const connActions = useConnectionsActions3();
  const buildingStore = mainLocationsStore.activeLocation?.activeBuilding;
  const rackStore = buildingStore?.rackStores.get(props.rack.id);
  useWatchNetwork(rackStore);

  useEffect(() => {
    const cb = (ev: MouseEvent) => {
      if (!self.current) return;
      const rect = self.current.getBoundingClientRect();
      if (
        ev.clientX < rect.left ||
        ev.clientX > rect.right ||
        ev.clientY < rect.top ||
        ev.clientY > rect.bottom
      ) {
        if (rackStore?.isReduced) setHeavy(false);
      } else {
        setHeavy(true);
      }
    };
    window.addEventListener("mousemove", cb);
    window.addEventListener("dragover", cb);
    return () => {
      window.removeEventListener("mousemove", cb);
      window.removeEventListener("dragover", cb);
    };
  }, []);

  useEffect(() => {
    if (rackStore) connActions?.changeRackItems(
      rackStore.rack.id,
      rackStore.rack.rack_items
    );
  }, [rackStore?.rack.rack_items]);

  useEffect(() => {
    rackStore?.createItemStores();
    return () => rackStore?.deleteItemStores();
  }, [rackStore]);

  return {
    rackStore,
    rack: rackStore?.rack,
    heavy,
    self,
    buildingStore,
  };
};

const Rack3 = observer((props: IRack3Props) => {
  const ctrl = useControl(props);

  return ctrl.rack
    ? (
      <div
        className={cn(styles.wrapper, {
          [styles.not_filtered]: ctrl.buildingStore?.hasSearch && !ctrl.buildingStore?.racks.has(ctrl.rack.id),
        })}
        ref={ctrl.self}
      >
        <div
          className={cn(styles.content, {
          })}
          style={{
            width: `${
              ctrl.rack.rack_type[0] * RACK_UNIT * 2 +
              RACK_UNIT * 2
            }px`,
          }} // ширина шкафа обязательно должна быть кратна RACK_UNIT
        >
          {SHOW_SCHEMES && (
            <>
              <div
                className={cn(styles.equipment, styles.left, {
                  [styles.visible]: ctrl.buildingStore?.isFlipped,
                })}
              >
                <BackIcon3 kind='rack' type={EQUIPMENT_TYPE.NETWORK} item={props.rack}/>
              </div>
              <div
                className={cn(styles.equipment, styles.right, {
                  [styles.visible]: ctrl.buildingStore?.isFlipped,
                })}
              >
                <BackIcon3 kind='rack' type={EQUIPMENT_TYPE.ELECTRICAL}  item={props.rack}/>
              </div>
            </>
          )}

          <RackHeader3 rackStore={ctrl.rackStore!} />

          <div
            className={styles.rack_items}
            style={{
              gridTemplateColumns: makeGrid(
                ctrl.rack.rack_type[0],
                `${2 * RACK_UNIT}px`
              ),
              gridTemplateRows: makeGrid(
                ctrl.rack.rack_type[1],
                "52px"
              ),
            }}
          > 
          {
            ctrl.rackStore?.itemStores?.map((itemStore, i) => <NumberOfColumn
                rack_type={ctrl.rack!.rack_type}
                index={i}
                key={itemStore.item.id}
              >
                { ctrl.heavy 
                  ? <RackItem3 itemStore={itemStore}/>
                  : <LightRackItem3 itemStore={itemStore}/>
                }
              </NumberOfColumn>
            )
          }
          </div>
        </div>
      </div>
    )
    : <></>;
});

Rack3.displayName = "Rack3";

export default memo(Rack3, (prev: IRack3Props, curr: IRack3Props) => {
  return JSON.stringify(prev) === JSON.stringify(curr);
});

